// src/components/ChartCategorySales.js
import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import axiosInstance from '../utils/axiosInstance';
import { plotlyStyle, formatLabel } from '../utils/plotlyStyle';
import { Stack, Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { MESSAGES } from '../constants/messages';
const ChartCategorySales = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const now = dayjs();
  const defaultFrom = now.subtract(2, 'month').startOf('month'); // 初期表示: 直近3か月
  const defaultTo = now.endOf('month');

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/chart/category-sales', { params });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
    });
  }, []);

  const setThisMonth = () => {
    const f = now.startOf('month');
    const t = now.endOf('month');
    setFrom(f);
    setTo(t);
    fetchData({ from: f.format('YYYY-MM-DD'), to: t.format('YYYY-MM-DD') });
  };

  const setLast3Months = () => {
    const f = now.subtract(2, 'month').startOf('month');
    const t = now.endOf('month');
    setFrom(f);
    setTo(t);
    fetchData({ from: f.format('YYYY-MM-DD'), to: t.format('YYYY-MM-DD') });
  };

  const setLast12Months = () => {
    const f = now.subtract(11, 'month').startOf('month');
    const t = now.endOf('month');
    setFrom(f);
    setTo(t);
    fetchData({ from: f.format('YYYY-MM-DD'), to: t.format('YYYY-MM-DD') });
  };

  const layout = {
    ...plotlyStyle.layout,
    title: {
      text: `商品カテゴリ別売上高 (${from.format('YYYY-MM')}〜${to.format('YYYY-MM')}）`,
    },
    xaxis: { title: 'カテゴリ名' },
    yaxis: {
      title: '売上高',
      tickformat: ',.0f',
      ticksuffix: '円',
      exponentformat: 'none',
      showexponent: 'none',
    },
    margin: { l: 150 },
    legend: { orientation: 'h', y: -0.25 },
  };
  // ローディングやエラー時の表示
  if (loading) return <Typography>{MESSAGES.LOADING}</Typography>;
  if (error) return <Typography>{error || MESSAGES.LOAD_FAILED}</Typography>;

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {/* 日付ピッカー */}
          <Stack direction="row" spacing={2} alignItems="center">
            <DatePicker
              label="開始月"
              views={['year', 'month']}
              value={from}
              onChange={setFrom}
              maxDate={to}
              format="YYYY年M月"
              slotProps={{ textField: { size: 'small' } }}
            />
            <Typography>〜</Typography>
            <DatePicker
              label="終了月"
              views={['year', 'month']}
              value={to}
              onChange={setTo}
              minDate={from}
              format="YYYY年M月"
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button
              size="small"
              variant="contained"
              disabled={!from || !to}
              onClick={() =>
                fetchData({
                  from: from.startOf('month').format('YYYY-MM-DD'),
                  to: to.endOf('month').format('YYYY-MM-DD'),
                })
              }
            >
              適用
            </Button>
          </Stack>

          {/* プリセットボタン */}
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={setThisMonth}>今月</Button>
            <Button size="small" variant="outlined" onClick={setLast3Months}>直近3か月</Button>
            <Button size="small" variant="outlined" onClick={setLast12Months}>直近12か月</Button>
          </Stack>
        </Stack>
      </LocalizationProvider>

      {loading && <Typography>Loading...</Typography>}
      {error && <Typography>{error}</Typography>}

      {!loading && !error && (
        <div>
          <Plot
            data={[
              {
                x: data.map(item => formatLabel(item.category_name)),
                y: data.map(item => Number(item.total_sales)),
                type: 'bar',
                name: '売上高',
                marker: { color: plotlyStyle.colors.bar },
                text: data.map(item => item.category_name),
                hovertemplate: '%{text}<br>売上高: %{y} 円<extra></extra>',
              },
            ]}
            layout={layout}
            style={{ width: '100%', height: '100%', minHeight: 500 }}
            useResizeHandler={true}
          />
        </div>
      )}
    </>
  );
};

export default ChartCategorySales;
